import { useEffect, useState, type ReactNode } from 'react'
import { useAppDispatch } from '@/app/hooks'
import { createSubDealer, saveSubDealer } from '@/features/dealers/dealersSlice'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Field, Switch, TextInput } from '@/components/ui/Field'
import { useAsyncAction } from '@/lib/useAsyncAction'
import type { SubDealer } from '@/types'

const MIN_PASSWORD = 8
const MAX_CLIENTS = 100

export interface SubDealerFormModalProps {
  open: boolean
  onClose: () => void
  /** Numeric parent id the new sub-dealer hangs off. */
  parentUserId: number
  /** Present when editing; absent when creating. */
  editing: SubDealer | null
}

interface FormState {
  name: string
  email: string
  password: string
  allowedClients: string
  isActive: boolean
}

const BLANK: FormState = {
  name: '',
  email: '',
  password: '',
  allowedClients: '5',
  isActive: true
}

export function SubDealerFormModal({
  open,
  onClose,
  parentUserId,
  editing
}: SubDealerFormModalProps): ReactNode {
  const dispatch = useAppDispatch()
  const { isPending, run } = useAsyncAction()
  const [form, setForm] = useState<FormState>(BLANK)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!open) return
    setTouched(false)
    setForm(
      editing
        ? {
            name: editing.name,
            email: editing.email,
            password: '',
            allowedClients: String(editing.allowed_clients),
            isActive: editing.is_active
          }
        : BLANK
    )
  }, [open, editing])

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const clients = Number(form.allowedClients)
  const errors = {
    name: form.name.trim().length < 2 ? 'At least 2 characters.' : undefined,
    email:
      !editing && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
        ? 'Enter a valid email address.'
        : undefined,
    password:
      !editing && form.password.length < MIN_PASSWORD
        ? 'At least ' + MIN_PASSWORD + ' characters.'
        : undefined,
    allowedClients:
      !Number.isFinite(clients) || clients < 1 || clients > MAX_CLIENTS
        ? 'Between 1 and ' + MAX_CLIENTS + '.'
        : undefined
  }
  const valid = Object.values(errors).every((e) => e === undefined)

  const submit = async (): Promise<void> => {
    setTouched(true)
    if (!valid) return
    const ok = editing
      ? await run('save', () =>
          dispatch(
            saveSubDealer({
              userId: editing.user_id,
              body: {
                name: form.name.trim(),
                allowed_clients: clients,
                is_active: form.isActive
              }
            })
          ).unwrap()
        )
      : await run('save', () =>
          dispatch(
            createSubDealer({
              parent_user_id: parentUserId,
              name: form.name.trim(),
              email: form.email.trim(),
              password: form.password,
              allowed_clients: clients
            })
          ).unwrap()
        )
    if (ok) onClose()
  }

  const show = (message?: string): string | undefined => (touched ? message : undefined)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit sub-dealer' : 'New sub-dealer'}
      description={
        editing
          ? editing.user_id + ' · ' + editing.email
          : 'Created verified, so they can sign in straight away.'
      }
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={isPending('save')}
            onClick={() => void submit()}
          >
            {editing ? 'Save changes' : 'Create sub-dealer'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Full name" error={show(errors.name)}>
          {(id) => (
            <TextInput
              id={id}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Priya Nair"
              autoFocus
            />
          )}
        </Field>

        {editing ? (
          <Field label="Email" hint="Email cannot be changed after creation.">
            {(id) => <TextInput id={id} value={form.email} disabled />}
          </Field>
        ) : (
          <>
            <Field label="Email" error={show(errors.email)}>
              {(id) => (
                <TextInput
                  id={id}
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  placeholder="priya@example.com"
                  autoComplete="off"
                  spellCheck={false}
                />
              )}
            </Field>
            <Field
              label="Password"
              hint={'At least ' + MIN_PASSWORD + ' characters. Share it with them directly.'}
              error={show(errors.password)}
            >
              {(id) => (
                <TextInput
                  id={id}
                  type="password"
                  value={form.password}
                  onChange={(e) => set('password', e.target.value)}
                  autoComplete="new-password"
                />
              )}
            </Field>
          </>
        )}

        <Field
          label="Allowed clients"
          hint={'How many trading clients they may connect (1–' + MAX_CLIENTS + ').'}
          error={show(errors.allowedClients)}
        >
          {(id) => (
            <TextInput
              id={id}
              type="number"
              min={1}
              max={MAX_CLIENTS}
              value={form.allowedClients}
              onChange={(e) => set('allowedClients', e.target.value)}
            />
          )}
        </Field>

        {editing && (
          <div className="rounded-md border border-line bg-raised p-3">
            <Switch
              checked={form.isActive}
              onChange={(next) => set('isActive', next)}
              label="Account active"
              description="Turning this off blocks their sign-in immediately."
            />
          </div>
        )}
      </div>
    </Modal>
  )
}
